import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { LOGO } from "@/lib/seo";

const KEY = "shadc_age_verified";
const TTL = 30 * 864e5;

const verified = () => {
  const t = Number(localStorage.getItem(KEY));
  return Boolean(t) && Date.now() - t < TTL;
};

export const AgeGate = () => {
  const [ok, setOk] = useState(verified);
  const [denied, setDenied] = useState(false);
  const box = useRef(null);

  useEffect(() => {
    if (ok) return undefined;
    document.body.style.overflow = "hidden";
    box.current?.querySelector("button")?.focus();
    const trap = (e) => {
      if (e.key !== "Tab" || !box.current) return;
      const f = box.current.querySelectorAll("button, a");
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", trap);
    return () => { document.body.style.overflow = ""; document.removeEventListener("keydown", trap); };
  }, [ok, denied]);

  if (ok) return null;
  const accept = () => { localStorage.setItem(KEY, String(Date.now())); setOk(true); window.dispatchEvent(new Event("shadc:age-verified")); };

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center overflow-y-auto bg-[#07080A]/95 p-5 backdrop-blur-xl" data-testid="age-gate">
      <div ref={box} role="dialog" aria-modal="true" aria-labelledby="age-title" aria-describedby="age-desc"
        className="relative w-full max-w-md animate-rise rounded-3xl border border-line bg-panel p-8 text-center shadow-2xl sm:p-10">
        <img src={LOGO} alt="SHADC Dispensary logo" width="120" height="120" className="logo-halo mx-auto h-28 w-28" />
        <p className="eyebrow mt-6">Adults only</p>
        {denied ? (
          <>
            <h2 id="age-title" className="mt-3 text-3xl font-semibold text-white">Sorry, you must be 21+</h2>
            <p id="age-desc" className="mt-3 text-base text-gray-400">This website is only available to adults 21 years of age or older.</p>
            <button type="button" onClick={() => setDenied(false)} className="btn-ghost mt-8 w-full" data-testid="age-gate-back-button">Go back</button>
          </>
        ) : (
          <>
            <h2 id="age-title" className="mt-3 text-3xl font-semibold text-white sm:text-4xl">Are you 21 or older?</h2>
            <p id="age-desc" className="mt-3 text-base text-gray-400">You must be 21+ to enter SHADC Dispensary. A valid government-issued ID is required for every order.</p>
            <div className="mt-8 grid gap-3">
              <button type="button" onClick={accept} className="btn-gold w-full" data-testid="age-gate-accept-button">Yes, I&apos;m 21 or older</button>
              <button type="button" onClick={() => setDenied(true)} className="btn-ghost w-full" data-testid="age-gate-deny-button">No, I&apos;m under 21</button>
            </div>
            <p className="mt-6 text-xs leading-relaxed text-gray-500">
              By entering, you agree to our <Link to="/terms/" className="underline">Terms</Link> and{" "}
              <Link to="/privacy-policy/" className="underline">Privacy Policy</Link>.
            </p>
          </>
        )}
      </div>
    </div>
  );
};
